import { useState, useEffect, useCallback, useRef } from 'react';
import RecipeCard from './RecipeCard';
import recipeAPI from '../services/api';
import { CATEGORIES } from '../constants/recipeConstants';

const RecipeList = ({ onRecipeClick, onCreateNew, onEdit }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('Tümü');
  const [recipes, setRecipes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRecipes, setTotalRecipes] = useState(0);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const observerTarget = useRef(null);

  const categories = ['Tümü', ...CATEGORIES];

  const loadRecipes = useCallback(async (isLoadMore = false, pageOverride = null) => {
    try {
      if (isLoadMore) {
        setIsLoadingMore(true);
      } else {
        setLoading(true);
      }
      setError('');
      
      const pageToLoad = pageOverride !== null ? pageOverride : (isLoadMore ? currentPage + 1 : currentPage);
      const params = { page: pageToLoad };
      if (selectedCategory !== 'Tümü') {
        params.category = selectedCategory;
      }
      if (searchTerm) {
        params.search = searchTerm;
      }
      const data = await recipeAPI.getRecipes(params);

      // API'den pagination bilgileri geliyorsa
      if (data.data && Array.isArray(data.data)) {
        if (isLoadMore) {
          setRecipes(prev => [...prev, ...data.data]);
          setCurrentPage(prev => prev + 1);
        } else {
          setRecipes(data.data);
          setCurrentPage(data.current_page || 1);
        }
        setTotalPages(Math.ceil((data.total || 0) / (data.per_page || 10)));
        setTotalRecipes(data.total || 0);
      } else {
        // Pagination olmayan eski format
        setRecipes(data.data || []);
      }
    } catch (err) {
      setError('Tarifler yüklenemedi. API sunucusunun çalıştığından emin olun.');
      console.error('Load recipes error:', err);
    } finally {
      setLoading(false);
      setIsLoadingMore(false);
    }
  }, [selectedCategory, searchTerm, currentPage]);

  useEffect(() => {
    setCurrentPage(1);
    setRecipes([]);
    loadRecipes(false, 1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCategory, searchTerm]);

  // Infinite scroll için Intersection Observer
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && !loading && !isLoadingMore && currentPage < totalPages) {
          loadRecipes(true);
        }
      },
      { threshold: 0.1 }
    );

    const currentTarget = observerTarget.current;
    if (currentTarget) {
      observer.observe(currentTarget);
    }

    return () => {
      if (currentTarget) {
        observer.unobserve(currentTarget);
      }
    };
  }, [loading, isLoadingMore, currentPage, totalPages, loadRecipes]);

  const handleDeleteClick = (recipe) => {
    setDeleteConfirm(recipe);
  };

  const handleDeleteConfirm = async () => {
    if (!deleteConfirm) return;

    try {
      await recipeAPI.deleteRecipe(deleteConfirm.id);
      setDeleteConfirm(null);
      loadRecipes();
    } catch (err) {
      setError('Tarif silinemedi: ' + err.message);
      setDeleteConfirm(null);
    }
  };

  const handleCategoryChange = (category) => {
    setSelectedCategory(category);
    setCurrentPage(1); // Kategori değiştiğinde ilk sayfaya dön
  };

  const filteredRecipes = recipes;

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 via-pink-50 to-purple-50">
      {/* Header */}
      <div className="bg-gradient-to-r from-purple-600 to-pink-600 text-white shadow-xl sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h1 className="text-3xl md:text-4xl font-bold mb-2">Yemek Tarifleri</h1>
              <p className="text-purple-100 text-sm md:text-base">
                {totalRecipes > 0 ? `${totalRecipes} tarif bulundu` : `${filteredRecipes.length} tarif bulundu`}
                {totalPages > 1 && ` • Sayfa ${currentPage}/${totalPages}`}
              </p>
            </div>
            <button
              onClick={onCreateNew}
              className="bg-white bg-opacity-20 hover:bg-opacity-30 backdrop-blur-sm px-4 py-2 rounded-lg transition-all duration-200 flex items-center gap-2"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              <span className="hidden sm:inline">Yeni Tarif</span>
            </button>
          </div>

          {/* Arama */}
          <div className="relative">
            <input
              type="text"
              placeholder="Tarif ara..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full px-4 py-3 pl-12 rounded-xl text-gray-800 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-300 transition-all"
            />
            <svg
              className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Error Message */}
        {error && (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded-2xl mb-6">
            {error}
          </div>
        )}

        {/* Kategori Filtreleri */}
        <div className="mb-8 overflow-x-auto pb-2">
          <div className="px-1 py-2 flex gap-3 min-w-max sm:min-w-0">
            {categories.map((category) => (
              <button
                key={category}
                onClick={() => handleCategoryChange(category)}
                className={`px-5 py-2 rounded-full font-medium transition-all duration-200 whitespace-nowrap ${
                  selectedCategory === category
                    ? 'bg-gradient-to-r from-purple-600 to-pink-600 text-white shadow-lg scale-105'
                    : 'bg-white text-gray-700 hover:bg-gray-50 shadow'
                }`}
              >
                {category}
              </button>
            ))}
          </div>
        </div>

        {/* Loading State */}
        {loading ? (
          <div className="flex justify-center items-center py-20">
            <div className="text-center">
              <div className="w-16 h-16 border-4 border-purple-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
              <p className="text-gray-600">Tarifler yükleniyor...</p>
            </div>
          </div>
        ) : filteredRecipes.length > 0 ? (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {filteredRecipes.map((recipe) => (
                <RecipeCard
                  key={recipe.id}
                  recipe={recipe}
                  onClick={() => onRecipeClick(recipe)}
                  onEdit={onEdit}
                  onDelete={handleDeleteClick}
                />
              ))}
            </div>
            
            {/* Infinite Scroll Trigger */}
            {currentPage < totalPages && (
              <div ref={observerTarget} className="flex justify-center items-center py-8">
                {isLoadingMore && (
                  <div className="text-center">
                    <div className="w-10 h-10 border-4 border-purple-600 border-t-transparent rounded-full animate-spin mx-auto mb-2"></div>
                    <p className="text-gray-600 text-sm">Daha fazla tarif yükleniyor...</p>
                  </div>
                )}
              </div>
            )}
          </>
        ) : (
          <div className="text-center py-20">
            <svg className="w-24 h-24 mx-auto text-gray-300 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <h3 className="text-xl font-semibold text-gray-600 mb-2">Tarif bulunamadı</h3>
            <p className="text-gray-500">Arama kriterlerinizi değiştirmeyi deneyin</p>
          </div>
        )}

        {/* Pagination - Removed */}
      </div>

      {/* Delete Confirmation Modal */}
      {deleteConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-2xl p-6 max-w-sm w-full shadow-2xl">
            <div className="text-center mb-6">
              <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
              </div>
              <h3 className="text-xl font-bold text-gray-800 mb-2">Tarifi Sil</h3>
              <p className="text-gray-600 mb-2">
                <span className="font-semibold">{deleteConfirm.title}</span> tarifini silmek istediğinizden emin misiniz?
              </p>
              <p className="text-gray-500 text-sm">Bu işlem geri alınamaz.</p>
            </div>
            <div className="flex gap-3">
              <button
                onClick={() => setDeleteConfirm(null)}
                className="flex-1 bg-gray-200 text-gray-800 py-3 rounded-xl font-semibold hover:bg-gray-300 transition-all"
              >
                İptal
              </button>
              <button
                onClick={handleDeleteConfirm}
                className="flex-1 bg-red-600 text-white py-3 rounded-xl font-semibold hover:bg-red-700 transition-all"
              >
                Sil
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RecipeList;
